/*
 * Copyright (c) 2020 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import { Injectable } from '@angular/core';
import { Logger } from '@core/lib/services';
import * as _ from 'underscore';
import { appConstants } from '@app/common/app-constants';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { HttpHeaders } from '@angular/common/http';
import { AppConfigService } from '@app/common/app-config.service';
import { TranslateService } from '@ngx-translate/core';
import { NotifyService } from '@app/common/notify.service';
import { Router } from '@angular/router';
import { UtilService } from '@app/util/util.service';

const log = new Logger('UserPrivilegesService');

@Injectable({
  providedIn: 'root'
})
export class UserPrivilegesService {
  privileges;
  institutes;
  private readonly loginMode;

  constructor(
    private router: Router,
    private dataService: ProxyDataService,
    private appConfigService: AppConfigService,
    private translate: TranslateService,
    private notify: NotifyService,
    private utilService: UtilService
  ) {
    this.loginMode = this.utilService.getQueryParam('mode');
  }

  getPrivileges() {
    const urlParams = this.dataService.transformUrlParams({
      username: this.appConfigService.getUserId(),
      loginMode: this.loginMode
    });
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
      })
    };
    return this.dataService
      .httpGet(
        encodeURI(appConstants.appURLs.securityPrivileges + (urlParams !== '' ? '?' + urlParams : '')),
        httpOptions
      )
      .then(
        response => {
          this.privileges = _.pluck(response.sqlPrivilegeUserDTOList, 'sqlPrivilegeName');
          this.institutes = _.values(response.institutes);
          this.privileges.concat('Send');
          this.privileges.concat('ID_ExceptionWL');
          this.appConfigService.setUserPrivileges(this.privileges);
        },
        error => {
          log.error('Error occurred while getting user privileges ' + JSON.stringify(error));
          if (error.status === 401) {
            this.translate
              .get(['imagingDesktop.error.unauthorized'])
              .toPromise()
              .then(translations => {
                this.notify.error(translations['imagingDesktop.error.unauthorized']);
              });
          } else {
            this.translate
              .get(['imagingDesktop.error.TomcatServerDown'])
              .toPromise()
              .then(translations => {
                this.notify.error(translations['imagingDesktop.error.TomcatServerDown']);
              });
          }
          const redirectTimeout = setTimeout(() => {
            clearTimeout(redirectTimeout);
            window.location.replace('/' + this.utilService.urlParams().replace('?', '&'));
          }, 3000);
        }
      );
  }

  hasPrivilege(privilege): boolean {
    return _.contains(this.privileges, privilege);
  }

  isAdmin() {
    return _.contains(this.privileges, appConstants.UserPrivilege.AD_ID_Access);
  }

  getUserPrivileges() {
    return this.privileges;
  }

  userCanViewConfidentialPatient(): boolean {
    return _.contains(this.privileges, appConstants.UserPrivilege.VIEWCONFIDENTIALPATIENT);
  }

  userCanMarkUnmarkConfidentialPatient(): boolean {
    return _.contains(this.privileges, appConstants.UserPrivilege.MARKUNMARKCONFIDENTIALPATIENT);
  }

  userCanCreateLinkedWorklist(): boolean {
    return _.contains(this.privileges, appConstants.UserPrivilege.ID_CREATELINKEDWORKLIST);
  }

  userCanCreateCustomWorklist(): boolean {
    return _.contains(this.privileges, appConstants.UserPrivilege.ID_CREATECUSTOMWORKLIST);
  }

  hasUserDigitalSignPrivilege(): boolean {
    return _.contains(this.privileges, appConstants.UserPrivilege.ID_SIGNREPORT);
  }
}
